import { Injectable } from '@angular/core';

import { Observable, forkJoin, take, of, merge } from 'rxjs';
import { tap, switchMap, map, shareReplay, filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { Style } from '../models/style.model';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { Template } from '../models/template.model';
import { TemplateApiService } from '../../../api/templates/template.api.service';
import { StylesApiService } from '../../../api/styles/styles.api.service';
import { AllResourceAbstractService } from '../../Basic/service/all-resource-abstract.service';
import { StyleApi } from '../../../api/styles/style-api.model';
import { ResourceAction } from '../../Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../Basic/enums/resource-actions.enum';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';

/**
 * Responsible for managing styles and their associated templates, primarily used in page builder block components.
 */
@Injectable()
export class StylesService extends AllResourceAbstractService<Style, StyleApi, Style, StyleApi> {

    all$: Observable<Style[]> = merge(
        this.getApiService().getList().pipe(
            map(list => list.map(item => this.constructListModel(item))),
            switchMap((styles) => this.refreshTemplates().pipe(
                map(templates => this.mergeStylesAndTemplates(styles, templates)),
            ))
        ),
        this.refreshAllManualSubject
    ).pipe(
        shareReplay(1)
    );

    constructor(
        private styleApiService: StylesApiService,
        private templateApiService: TemplateApiService,
        private messageService: MessageService,
        private localizationService: LocalizationService
    ) {
        super();
    }

    getApiService(): MultiBaseAbstractApi<StyleApi, StyleApi> {
        return this.styleApiService;
    }

    constructResourceModel(apiModel: StyleApi): Style {
        return new Style(apiModel);
    }

    constructListModel(apiModel: StyleApi): Style {
        return new Style(apiModel);
    }


    override refreshAllManual(): Observable<Style[]> {
        return this.refreshStyles().pipe(
            switchMap((styles) => this.refreshTemplates().pipe(
                map(templates => this.mergeStylesAndTemplates(styles, templates)),
            )),
            tap((list) =>  this.refreshAllManualSubject.next(list))
        );
    }

    refreshStyles(): Observable<Style[]> {
        return this.getApiService().getList().pipe(
            map(list => list.map(item => this.constructListModel(item))),
        );
    }

    refreshTemplates(): Observable<Template[]> {
        return this.templateApiService.getList().pipe(
            map(list => list.map((item) => new Template(item)))
        );
    }

    getById(id: number): Observable<Style> {
        return this.all$.pipe(
            map((styles: Style[]) => {
                return styles.find(style => style.id === id);
            }),
            take(1)
        );
    }

    create(style: Style): Observable<number> {
        return this.createResource(style).pipe(
            switchMap((id) => {
                if (style.template) {
                    return this.saveTemplate(style, id).pipe(
                        map(x => id)
                    );
                } else {
                    return of(id);
                }
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
        );
    }

    /**
     * Update value of an existing style, create or update template separately
     * @return number - updated style id
     */
    update(style: Style): Observable<number> {
        return this.updateResource(style, { actionType: ResourceActionTypes.UPDATE}).pipe(
            switchMap((id) => {
                if (style.template) {
                    return this.saveTemplate(style, style.id).pipe(
                        map(() => id)
                    );
                } else {
                    return of(id);
                }
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
        );
    }


    /**
     * Delete the passed styles and their templates
     */
    bulkDelete(styles: Style[]): Observable<number[]> {
        const action = {
            actionType: ResourceActionTypes.DELETE,
            count: styles.length
        };
        return this.bulkDeleteResource(styles, action).pipe(
            switchMap(() => this.refreshTemplates()),
            switchMap((templateList) => {
                const templates = [];

                // find the templates related to those styles, and delete those templates as well.
                styles.forEach(style => {
                    templateList.forEach(template => {
                        if (template.styleId === style.id) {
                            templates.push(template);
                        }
                    });
                });

                if (templates.length === 0) {
                    return of(null);
                }

                return forkJoin(templates.map(template => this.templateApiService.delete(template)));
            }),
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(() => styles.map(x => x.id))
                );
            })
        );
    }


    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as Style;

        switch(actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Styles.createErrorHeading',
                        detail: this.localizationService.transform('Styles.createErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Styles.createSucessHeading',
                        detail: this.localizationService.transform('Styles.createSucessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Styles.updateErrorHeading',
                        detail: this.localizationService.transform('Styles.updateErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Styles.updateSucessHeading',
                        detail: this.localizationService.transform('Styles.updateSucessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }
            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Styles.deleteErrorHeading',
                        detail: this.localizationService.transform('Styles.deleteErrorMessage', {
                            count: actionResult?.count
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Styles.deleteSucessHeading',
                        detail: this.localizationService.transform('Styles.deleteSucessMessage', {
                            count: actionResult?.count
                        })
                    });
                }

                break;
            }
        }
    }

    private saveTemplate(style: Style, styleId?: number): Observable<number> {
        const templateApiModel = style.template.toApiModel();
        templateApiModel.type = style.type;
        if (styleId) {
            templateApiModel.styleIds = [styleId];
        }

        // probably should deal with the errors somehow... but it's hard
        if (templateApiModel.id) {
            return this.templateApiService.update(templateApiModel);
        } else {
            return this.templateApiService.create(templateApiModel);
        }
    }

    /**
     * Iterates over the templates and adds them to the style model they belong to
     */
    private mergeStylesAndTemplates(styleList: Style[], templateList: Template[]): Style[] {
        for (const template of templateList) {
            const style: Style = styleList.find(x => x.id === template.styleId);

            if (style) {
                style.template = template;
            }
        }

        return styleList;
    }
}

